import {Input} from "../types/Input";
import {useCallback, useMemo, useState} from "react";
import {InputType} from "../types/InputType";
import MaterialSelect from "./MaterialSelect";
import MTInput from "./MTInput";
import MaterialTextArea from "./MaterialTextArea";
import {getError, hasError} from "../hooks/errors";
import {RootState} from "../store";
import {useSelector} from 'react-redux'
import {useTranslation} from "react-i18next";

interface GenericInputsProps {
    inputs: Input[] | null,
    setLocalValue: (key: string, value: string | number) => void,
    values?: object,
    className?: string

}

export default function GenericInputs({inputs, setLocalValue, values, className}: GenericInputsProps) {

    const {t} = useTranslation();
    const errors = useSelector((state: RootState) => state.validation.errors);
    const [inputValues, setInputValues] = useState<object>(values ?? {});

    const setValue = useCallback((key: string, value: string | number) => {
        setInputValues({...inputValues, [key as keyof typeof inputValues]: value});
        setLocalValue(key, value);
    }, [inputValues, setInputValues, setLocalValue]);

    const shouldShow = useCallback((key: string, value: string): boolean => {
        if (key.length === 0)
            return true;
        return inputValues !== null && typeof inputValues[key] !== "undefined" && inputValues[key] === value;
    }, [inputValues]);

    const content = useMemo(() => {
        if (inputs === null || inputs === undefined)
            return <></>;
        return inputs.map((input: Input) => {
            switch (input.type) {
                case InputType.select:
                    return (
                        <div key={'key-' + input.key + (input.show_when ? '-' + input.show_when : '')}
                             className={`text-left items-center mb-4 ${!shouldShow(input.depends ?? '', input.show_when ?? '') ? 'hidden' : ''}`}>
                            <MaterialSelect onChange={setValue} key={input.key}
                                            options={input.options ?? []}
                                            required={true} name={input.key}
                                            placeholder={input.label}
                                            value={inputValues[input.key]}
                            />
                            {hasError(input.key, errors) &&
                                <label key={'error-' + input.key}
                                       className="mt-1 text-red-500 text-xs block">{getError(input.key, errors)}</label>}
                        </div>
                    )
                case InputType.input:
                case InputType.number:
                case InputType.phone:
                case InputType.email:
                case InputType.password:
                    return (
                        <div key={'key-' + input.key + (input.show_when ? '-' + input.show_when : '')}
                             className={`text-left mb-4`}>
                            <MTInput type={input.type === InputType.input ? 'text' : input.type}
                                     className={className}
                                     onChange={setValue} key={input.key} name={input.key}
                                     value={inputValues[input.key]}
                                     placeholder={t('input.enter',{label: input.label})} label={input.label}/>
                            {hasError(input.key, errors) &&
                                <label key={'error-' + input.key}
                                       className="mt-1 text-red-500 text-xs block">{getError(input.key, errors)}</label>}
                        </div>
                    )
                case InputType.textarea:
                    return (
                        <div key={'key-' + input.key + (input.show_when ? '-' + input.show_when : '')}
                             className={`text-left mb-4`}>
                            <MaterialTextArea onChange={setLocalValue} key={input.key} name={input.key} value={inputValues[input.key]}
                                              label={input.label}/>
                            {hasError(input.key, errors) &&
                                <label key={'error-' + input.key}
                                       className="mt-1 text-red-500 text-xs block">{getError(input.key, errors)}</label>}
                        </div>
                    )
                default:
                    return null
            }
        })
    }, [errors, inputs, setLocalValue, setValue, shouldShow])

    return (
        <>
            {content}
        </>
    );
}
